/**
 * Filterable Content JavaScript
 *
 * Handles AJAX filtering, Load More pagination, Infinite Scroll,
 * and Back to Top functionality for filterable content components.
 *
 * @package Boilerplate
 * @since Plan 40
 */

(function($) {
	'use strict';

	// Configuration
	const config = {
		// Selectors
		filterTabs: '.filter-tabs',
		filterDropdown: '.filter-tabs__mobile select',
		filterTab: '.filter-tabs__desktop a',
		contentGrid: '[data-filterable-content]',
		pagination: '.pagination',
		loadMoreBtn: '.pagination__load-more',
		infiniteTrigger: '.pagination__infinite-trigger',
		backToTop: '.back-to-top',

		// Settings
		infiniteThreshold: 200, // px from bottom to trigger
		backToTopThreshold: 400, // px to show button
		animationDuration: 300, // ms
	};

	// State
	let isLoading = false;
	let infiniteScrollObserver = null;

	/**
	 * Initialize all filterable content functionality
	 */
	function init() {
		initAjaxFiltering();
		initLoadMore();
		initInfiniteScroll();
		initBackToTop();
	}

	/**
	 * Initialize AJAX filtering for filter tabs
	 */
	function initAjaxFiltering() {
		const $filterTabs = $(config.filterTabs + '[data-ajax="true"]');

		if (!$filterTabs.length) return;

		// Desktop: Tab clicks
		$filterTabs.on('click', config.filterTab, function(e) {
			e.preventDefault();

			if (isLoading) return;

			const $tab = $(this);
			const url = $tab.attr('href');

			// Update active state
			$(config.filterTab).attr('aria-selected', 'false').removeAttr('data-active');
			$tab.attr('aria-selected', 'true').attr('data-active', 'true');

			// Load content
			loadFilteredContent(url);
		});

		// Mobile: Dropdown change
		$filterTabs.on('change', config.filterDropdown, function() {
			const $select = $(this);
			const category = $select.val();
			const $form = $select.closest('form');
			const baseUrl = $form.attr('action');

			// Build URL
			let url = baseUrl;
			if (category && category !== 'all') {
				url += (baseUrl.includes('?') ? '&' : '?') + 'category=' + encodeURIComponent(category);
			}

			loadFilteredContent(url);
		});
	}

	/**
	 * Load filtered content via AJAX
	 * @param {string} url - URL to fetch content from
	 */
	function loadFilteredContent(url) {
		const $grid = $(config.contentGrid);
		const $liveRegion = $(config.filterTabs).find('.sr-only');

		if (!$grid.length || isLoading) return;

		isLoading = true;

		// Show loading state
		$grid.addClass('loading').css('opacity', '0.5');
		$liveRegion.text('Loading content...');

		$.ajax({
			url: url,
			type: 'GET',
			dataType: 'html',
			success: function(response) {
				const $response = $(response);
				const $newGrid = $response.find(config.contentGrid);
				const $newPagination = $response.find(config.pagination);

				// Update content
				if ($newGrid.length) {
					$grid.html($newGrid.html()).removeClass('loading').css('opacity', '1');
				}

				// Update pagination
				if ($newPagination.length) {
					$(config.pagination).replaceWith($newPagination);
					// Re-initialize pagination handlers
					initLoadMore();
					initInfiniteScroll();
				} else {
					$(config.pagination).remove();
				}

				// Update URL (browser history)
				window.history.pushState({}, '', url);

				// Announce to screen readers
				const itemCount = $grid.find('.blog-card, .post-card, [class*="-card"]').length;
				$liveRegion.text('Content updated. Showing ' + itemCount + ' items.');
			},
			error: function() {
				$grid.removeClass('loading').css('opacity', '1');
				$liveRegion.text('Error loading content. Please try again.');
			},
			complete: function() {
				isLoading = false;
			}
		});
	}

	/**
	 * Initialize Load More button functionality
	 */
	function initLoadMore() {
		const $loadMore = $(config.loadMoreBtn);

		if (!$loadMore.length) return;

		$loadMore.off('click').on('click', function(e) {
			e.preventDefault();

			if (isLoading) return;

			const $btn = $(this);
			const currentPage = parseInt($btn.data('page'), 10);
			const maxPages = parseInt($btn.data('max-pages'), 10);
			const nextPage = currentPage + 1;

			if (nextPage > maxPages) {
				$btn.hide();
				return;
			}

			loadMorePosts($btn, nextPage, maxPages);
		});
	}

	/**
	 * Load more posts via AJAX
	 * @param {jQuery} $btn - Load more button element
	 * @param {number} page - Page number to load
	 * @param {number} maxPages - Maximum pages available
	 */
	function loadMorePosts($btn, page, maxPages) {
		const $grid = $(config.contentGrid);
		const $liveRegion = $(config.pagination).find('.sr-only');

		if (!$grid.length || isLoading) return;

		isLoading = true;

		// Show loading state
		$btn.addClass('loading').prop('disabled', true);
		$liveRegion.text('Loading more content...');

		// Build URL for next page
		const currentUrl = window.location.href;
		const url = currentUrl.includes('?')
			? currentUrl.replace(/([?&])paged=\d+/, '$1paged=' + page).replace(/\?&/, '?')
			: currentUrl + (currentUrl.includes('?') ? '&' : '?') + 'paged=' + page;

		$.ajax({
			url: url,
			type: 'GET',
			dataType: 'html',
			success: function(response) {
				const $response = $(response);
				const $newGrid = $response.find(config.contentGrid);

				// Append new content
				if ($newGrid.length) {
					const $newItems = $newGrid.children();
					$newItems.css('opacity', '0');
					$grid.append($newItems);

					// Animate in new items
					$newItems.animate({ opacity: 1 }, config.animationDuration);

					// Update button state
					$btn.data('page', page).removeClass('loading').prop('disabled', false);

					// Hide button if on last page
					if (page >= maxPages) {
						$btn.fadeOut(config.animationDuration);
					}

					// Announce to screen readers
					$liveRegion.text('Loaded ' + $newItems.length + ' more items.');
				}
			},
			error: function() {
				$btn.removeClass('loading').prop('disabled', false);
				$liveRegion.text('Error loading more content. Please try again.');
			},
			complete: function() {
				isLoading = false;
			}
		});
	}

	/**
	 * Initialize Infinite Scroll functionality
	 */
	function initInfiniteScroll() {
		const $trigger = $(config.infiniteTrigger);

		if (!$trigger.length) return;

		// Use IntersectionObserver for performance
		if ('IntersectionObserver' in window) {
			// Disconnect existing observer
			if (infiniteScrollObserver) {
				infiniteScrollObserver.disconnect();
			}

			infiniteScrollObserver = new IntersectionObserver(function(entries) {
				entries.forEach(function(entry) {
					if (entry.isIntersecting && !isLoading) {
						const $element = $(entry.target);
						const currentPage = parseInt($element.data('page'), 10);
						const maxPages = parseInt($element.data('max-pages'), 10);
						const nextPage = currentPage + 1;

						if (nextPage <= maxPages) {
							loadInfiniteContent($element, nextPage, maxPages);
						} else {
							// No more pages - hide trigger
							$element.hide();
							infiniteScrollObserver.unobserve(entry.target);
						}
					}
				});
			}, {
				rootMargin: config.infiniteThreshold + 'px'
			});

			infiniteScrollObserver.observe($trigger[0]);
		} else {
			// Fallback: Scroll event listener
			$(window).on('scroll.infiniteScroll', throttle(function() {
				if (isLoading) return;

				const scrollPos = $(window).scrollTop() + $(window).height();
				const triggerPos = $trigger.offset().top - config.infiniteThreshold;

				if (scrollPos >= triggerPos) {
					const currentPage = parseInt($trigger.data('page'), 10);
					const maxPages = parseInt($trigger.data('max-pages'), 10);
					const nextPage = currentPage + 1;

					if (nextPage <= maxPages) {
						loadInfiniteContent($trigger, nextPage, maxPages);
					} else {
						$trigger.hide();
						$(window).off('scroll.infiniteScroll');
					}
				}
			}, 100));
		}
	}

	/**
	 * Load infinite scroll content
	 * @param {jQuery} $trigger - Trigger element
	 * @param {number} page - Page number to load
	 * @param {number} maxPages - Maximum pages available
	 */
	function loadInfiniteContent($trigger, page, maxPages) {
		const $grid = $(config.contentGrid);
		const $liveRegion = $(config.pagination).find('.sr-only');

		if (!$grid.length || isLoading) return;

		isLoading = true;
		$liveRegion.text('Loading more content...');

		// Build URL for next page
		const currentUrl = window.location.href;
		const url = currentUrl.includes('paged=')
			? currentUrl.replace(/paged=\d+/, 'paged=' + page)
			: currentUrl + (currentUrl.includes('?') ? '&' : '?') + 'paged=' + page;

		$.ajax({
			url: url,
			type: 'GET',
			dataType: 'html',
			success: function(response) {
				const $response = $(response);
				const $newGrid = $response.find(config.contentGrid);

				// Append new content
				if ($newGrid.length) {
					const $newItems = $newGrid.children();
					$newItems.css('opacity', '0');
					$grid.append($newItems);

					// Animate in new items
					$newItems.animate({ opacity: 1 }, config.animationDuration);

					// Update trigger state
					$trigger.data('page', page);

					// Hide trigger if on last page
					if (page >= maxPages) {
						$trigger.fadeOut(config.animationDuration);
						if (infiniteScrollObserver) {
							infiniteScrollObserver.unobserve($trigger[0]);
						}
					}

					// Announce to screen readers
					$liveRegion.text('Loaded ' + $newItems.length + ' more items.');
				}
			},
			error: function() {
				$liveRegion.text('Error loading more content.');
			},
			complete: function() {
				isLoading = false;
			}
		});
	}

	/**
	 * Initialize Back to Top button functionality
	 */
	function initBackToTop() {
		const $backToTop = $(config.backToTop);

		if (!$backToTop.length) return;

		// Fixed position buttons: Show/hide on scroll
		const $fixedBtn = $backToTop.filter('[data-position="fixed"]');
		if ($fixedBtn.length) {
			$(window).on('scroll.backToTop', throttle(function() {
				if ($(window).scrollTop() > config.backToTopThreshold) {
					$fixedBtn.addClass('visible');
				} else {
					$fixedBtn.removeClass('visible');
				}
			}, 100));
		}

		// Click handler: Smooth scroll to top
		$backToTop.on('click', function(e) {
			e.preventDefault();
			window.scrollTo({ top: 0, behavior: 'smooth' });
			// Move focus to top of page for accessibility after scroll completes
			window.addEventListener('scrollend', function onScrollEnd() {
				window.removeEventListener('scrollend', onScrollEnd);
				document.body.setAttribute('tabindex', '-1');
				document.body.focus();
				document.body.removeAttribute('tabindex');
			}, { once: true });
		});
	}

	/**
	 * Throttle function for scroll events
	 * @param {Function} func - Function to throttle
	 * @param {number} wait - Milliseconds to wait
	 * @returns {Function}
	 */
	function throttle(func, wait) {
		let timeout = null;
		let lastCall = 0;

		return function(...args) {
			const now = Date.now();
			const remaining = wait - (now - lastCall);

			if (remaining <= 0 || remaining > wait) {
				if (timeout) {
					clearTimeout(timeout);
					timeout = null;
				}
				lastCall = now;
				func.apply(this, args);
			} else if (!timeout) {
				timeout = setTimeout(function() {
					lastCall = Date.now();
					timeout = null;
					func.apply(this, args);
				}, remaining);
			}
		};
	}

	// Initialize on document ready
	$(document).ready(init);

	// Re-initialize on ACF block preview (for editor)
	if (window.acf) {
		window.acf.addAction('render_block_preview', init);
	}

})(jQuery);
